import React, { FC } from 'react';
import { reduxForm, Field } from 'redux-form';
import { Toggle, newThemeInput } from '_fields/inputs';
import { connect } from 'react-redux';
import { formValueSelector } from 'redux-form';
import { Form } from '@components/shadcn/components/ui/form';
import { RootState } from '@store/reducers';
import { Helmet } from 'react-helmet';
import { Text } from 'shared-components';
import withOnSubmit from '@components/Apply/withOnSubmit/withOnSubmit';
import { ApplyWizardSteps, STEP_TYPE_OF_FUNDING } from '@components/ApplyWizard/ApplyWizardSteps';
import { useForm } from 'react-hook-form';
import { reduxFormZodValidate } from '@utils/reduxFormZodValidate';
import { useApplyWizardContext } from '../../../context/ApplyWizardContext';
import { LoanWizardShell, LoanSubmitButton, getLoanPhaseIndex } from '@components/Apply/WizardShell';

type TypeOfFundingProps = {
  handleSubmit: () => void;
  invalid: boolean;
  loanType?: string;
  saving?: boolean;
};

const TypeOfFundingWrapper2: FC<TypeOfFundingProps> = ({ handleSubmit, invalid, loanType, saving: savingOnSubmit }) => {
  const form = useForm();
  const { saving, currentPath } = useApplyWizardContext();
  const phase = getLoanPhaseIndex(currentPath);
  const hasEq = Array.isArray(loanType) ? loanType.some((val) => val?.toUpperCase() === 'EQ') : false;
  const isSubmitting = !!(saving || savingOnSubmit);

  return (
    <LoanWizardShell currentStepIndex={phase}>
      <Helmet>
        <title>Select Type of Funding</title>
      </Helmet>

      <div className="flex items-center justify-center py-[20px] w-full">
        <Text variant="headline-sm" className="!font-extended !uppercase !text-[16px] !leading-none text-center">
          Financing Type
        </Text>
      </div>

      <div className="flex flex-col gap-[10px] items-center w-full">
        <div className="flex items-center justify-center py-[10px] w-full">
          <Text variant="body-general" className="!text-[20px] !leading-[30px] text-center">
            Select all products you&apos;re interested in
          </Text>
        </div>

        <Form {...form}>
          <form onSubmit={handleSubmit} className="w-full">
            <div className="w-full flex flex-col gap-[10px]">
              <div className="w-full">
                <Field
                  component={Toggle}
                  className="grid-cols-1 sm:grid-cols-2 w768:grid-cols-2 w1024:grid-cols-3 sm:auto-rows-fr sm:h-full gap-[10px]"
                  itemClassName="!h-full min-h-[64px] !p-[16px] sm:h-full w768:h-full w1024:h-full"
                  name="loan_type"
                  defaultValue={loanType}
                  buttons={[
                    { text: `Business Credit Monitoring`, value: 'BCM' },
                    { text: `Working Capital`, value: 'WC' },
                    { text: `Line of Credit`, value: 'LOC' },
                    { text: `Equipment Financing`, value: 'EQ' },
                    { text: `Business Credit Card`, value: 'BCC' },
                    { text: `Personal Loan`, value: 'PL' },
                  ]}
                  allowMultiple
                />
              </div>

              {hasEq && (
                <div className="w-full max-w-[368px] mx-auto">
                  <span className="font-condensed text-[14px] text-charcoal leading-[1.2] block text-left pb-[6px] w-full">
                    EQUIPMENT NAME
                  </span>
                  <Field
                    name="equipment_description"
                    component={newThemeInput}
                    type="text"
                    placeholder="Enter equipment description"
                    labelText="Equipment name"
                    labelClassName="infoFormCustomLabel"
                    data-testid="equipment_description"
                    className="equipment-description !bg-dawn3"
                  />
                </div>
              )}

              <div className="flex flex-col items-center justify-center w-full pt-[20px] pb-[10px] px-[10px] gap-[10px]">
                <LoanSubmitButton invalid={invalid} saving={isSubmitting} data-testid="info_continue">
                  Continue
                </LoanSubmitButton>
              </div>
            </div>
          </form>
        </Form>
      </div>
    </LoanWizardShell>
  );
};

const TypeOfFundingWrapper1 = reduxForm<TypeOfFundingProps, TypeOfFundingProps>({
  form: 'application',
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true,
  validate: reduxFormZodValidate(ApplyWizardSteps[STEP_TYPE_OF_FUNDING].zodSchema),
})(TypeOfFundingWrapper2);
const appSelector = formValueSelector('application');

const mapStateToProps = (state: RootState) => {
  return {
    loanType: appSelector(state, 'loan_type'),
  };
};

export const TypeOfFunding = withOnSubmit(connect(mapStateToProps)(TypeOfFundingWrapper1), STEP_TYPE_OF_FUNDING);
